import type { HistoryEntry } from "../state/historyStore";

/**
 * The session summary (GRA-138): pure derivation over the history log,
 * filtered to one session (state/sessionStore's `startedAt`). Kept separate
 * from any store so the numbers are provably just a fold over the same
 * entries the history panel renders, never a second source that could
 * disagree with what's on screen.
 */

export type SessionSummary = {
  /**
   * Every counted entry in this session, any mode. Practice replays of an
   * already-recorded Daily challenge are not counted (GRA-154): nothing a
   * practice run does is kept, so folding it into the sitting's accuracy and
   * streak would make those numbers say something they do not mean. They are
   * reported on their own line as `practice` instead.
   */
  rounds: number;
  /** Predict entries this session ("correct"/"incorrect" outcomes only). */
  graded: number;
  correct: number;
  /** `correct / graded`, or null when no Predict round has been played yet. */
  accuracy: number | null;
  /**
   * Consecutive successes counting back from the most recent entry, across
   * every mode played this session. A win/correct extends it; a loss,
   * incorrect, or flat close breaks it (flat is not a success).
   */
  streak: number;
  /** Trade entries this session. */
  trades: number;
  /** Sum of Trade `pnl` this session (entries missing it, pre-GRA-138, count as 0). */
  pnl: number;
  /** Practice Rounds this session, counted apart from everything above. */
  practice: number;
};

const SUCCESS = new Set(["correct", "win"]);

/** Entries at or after `sessionStart`, from a newest-first history log. */
export function sessionEntries(entries: HistoryEntry[], sessionStart: number): HistoryEntry[] {
  return entries.filter((e) => e.timestamp >= sessionStart);
}

/** Fold a (newest-first) list of session entries into the summary numbers. */
export function summarizeSession(entries: HistoryEntry[], sessionStart: number): SessionSummary {
  const all = sessionEntries(entries, sessionStart);
  const practice = all.filter((e) => e.kind === "practice").length;
  const session = all.filter((e) => e.kind !== "practice");

  let graded = 0;
  let correct = 0;
  let trades = 0;
  let pnl = 0;
  for (const e of session) {
    if (e.outcome === "correct" || e.outcome === "incorrect") {
      graded += 1;
      if (e.outcome === "correct") correct += 1;
    }
    if (e.mode === "trade") {
      trades += 1;
      pnl += e.pnl ?? 0;
    }
  }

  let streak = 0;
  for (const e of session) {
    if (!SUCCESS.has(e.outcome)) break;
    streak += 1;
  }

  return {
    rounds: session.length,
    graded,
    correct,
    accuracy: graded > 0 ? correct / graded : null,
    streak,
    trades,
    pnl,
    practice,
  };
}
